import { format, parse } from 'date-fns'
import { tr } from 'date-fns/locale'

/**
 * Format currency value with localization
 * @param {number} value
 * @param {string} currency - 'TL', 'EUR', 'USD', 'GBP'
 * @returns {string}
 */
export const formatCurrency = (value, currency = 'TL') => {
  if (value === null || value === undefined) return '-'

  // Normalize: canias TL, MT940 TRY — ikisi de ₺
  if (currency === 'TRY') currency = 'TL'

  const symbols = {
    TL: '₺',
    EUR: '€',
    USD: '$',
    GBP: '£',
    AED: 'AED',
  }

  const symbol = symbols[currency] || currency

  // Simgesi olmayan para birimi (AED/dirhem) — Türk sayı biçimi + KOD sonda:
  // "1.234,56 AED". Varsayılan dal `AED1234.56` basıyordu (İngiliz ondalığı,
  // binlik ayracı yok, kod başta) — teklif belgesinde okunmuyordu.
  if (currency === 'AED') {
    return `${new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value)} AED`
  }

  if (currency === 'TL') {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  if (currency === 'EUR') {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  if (currency === 'USD' || currency === 'GBP') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  return `${symbol}${value.toFixed(2)}`
}

/**
 * Format number with thousand separator
 * @param {number} value
 * @returns {string}
 */
/**
 * Değişiklik/alan değeri — sayıysa tr-TR (ondalık VİRGÜL, binlik nokta), değilse ham metin.
 * Ürün onaylarında değerler ham geliyordu ("0.0000 → 600.0"); uygulamanın geri kalanı
 * virgül kullanırken burada nokta görünüyordu (21.08.2026 kullanıcı bildirimi).
 * Gereksiz ondalık kırpılır: 70,00 → 70 · 8156.08 → 8.156,08
 */
export const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'))
  if (!Number.isFinite(n)) return String(value)
  const txt = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(n)
  return txt
}

export const formatNumber = (value) => {
  if (value === null || value === undefined) return '-'

  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Dosya boyutu — bayt → okunabilir (tr-TR ondalık virgülü).
 * 0 geçerli bir boyuttur ('0 B'), yalnız null/undefined '-' döner.
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return '-'
  const b = Number(bytes)
  if (b < 1024) return `${b} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let val = b / 1024
  let i = 0
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024
    i += 1
  }
  const digits = val < 10 ? 1 : 0
  return `${val.toFixed(digits).replace('.', ',')} ${units[i]}`
}

/**
 * Format date to DD.MM.YYYY
 * @param {Date|string} date
 * @returns {string}
 */
export const formatDate = (date) => {
  if (!date) return '-'

  try {
    let d
    if (typeof date === 'string') {
      // Handle both "yyyy-MM-dd" and "yyyy-MM-ddTHH:mm:ss" formats
      d = date.includes('T') ? new Date(date) : parse(date, 'yyyy-MM-dd', new Date())
    } else {
      d = new Date(date)
    }
    if (isNaN(d.getTime())) return '-'
    return format(d, 'dd.MM.yyyy', { locale: tr })
  } catch (error) {
    console.error('Format date error:', error)
    return '-'
  }
}

/**
 * Format date and time to DD.MM.YYYY HH:mm
 * @param {Date|string} date
 * @returns {string}
 */
export const formatDateTime = (date) => {
  if (!date) return '-'

  try {
    let d
    if (typeof date === 'string') {
      d = date.includes('T') ? new Date(date) : parse(date, 'yyyy-MM-dd HH:mm:ss', new Date())
    } else {
      d = new Date(date)
    }
    if (isNaN(d.getTime())) return '-'
    return format(d, 'dd.MM.yyyy HH:mm', { locale: tr })
  } catch (error) {
    console.error('Format date time error:', error)
    return '-'
  }
}

/**
 * Format percentage value
 * @param {number} value
 * @returns {string}
 */
export const formatPercent = (value) => {
  if (value === null || value === undefined) return '-'

  return new Intl.NumberFormat('tr-TR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100)
}

/**
 * Kâr oranı / barem gösterimi — TEK KAYNAK.
 * Ondalık VİRGÜL (tr) ve en çok 2 basamak; gereksiz sıfır basılmaz:
 *   0.9901 → "%0,99"   4.15 → "%4,15"   10 → "%10"
 * formatPercent 1 basamağa yuvarladığı için %0,99'luk bir teklif ekranda "%1,0"
 * görünüyor, aynı sayfadaki kural metniyle ("%0,99") çelişiyor ve baremin ALTINDA
 * olan teklif tam baremdeymiş gibi okunuyordu. Marj/barem gösteren her yer bunu kullanır.
 * @param {number|null} value - yüzde DEĞERİ (0.99 = %0,99)
 * @returns {string}
 */
export const formatMarginPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return '%' + Number(value).toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Get color class based on positive/negative direction
 * @param {number} direction - 1 for positive (green), -1 for negative (red), 0 for neutral (gray)
 * @returns {string}
 */
export const getChangeColor = (direction) => {
  if (direction > 0) return 'text-accent-green'
  if (direction < 0) return 'text-accent-red'
  return 'text-text-muted'
}

/**
 * Get badge color class based on status
 * @param {string} status
 * @returns {string}
 */
export const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase() || ''

  if (statusLower.includes('onaylı') || statusLower.includes('tamamlanmış') || statusLower.includes('success')) {
    return 'badge-success'
  }
  if (statusLower.includes('beklemede') || statusLower.includes('pending') || statusLower.includes('warning')) {
    return 'badge-warning'
  }
  if (statusLower.includes('reddedilmiş') || statusLower.includes('rejected') || statusLower.includes('error')) {
    return 'badge-danger'
  }

  return 'badge-info'
}

/**
 * Truncate text to specified length
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Sütun başlığı stili: ilk harf büyük, kalanı küçük (cümle düzeni). Kısaltmalar korunur.
// "Stok Durumu" → "Stok durumu", "Birim İskonto" → "Birim iskonto", "KDV %" → "KDV %".
const _HEADER_ABBR = new Set(['KDV', 'SKU', 'TCV', 'URL', 'EAN', 'ID', 'OEM', 'PN'])
export const headerCase = (s) => {
  if (!s) return s
  return String(s).split(' ').map((w, i) => {
    if (!w) return w
    if (_HEADER_ABBR.has(w.toLocaleUpperCase('tr'))) return w.toLocaleUpperCase('tr')
    if (i === 0) return w.charAt(0).toLocaleUpperCase('tr') + w.slice(1).toLocaleLowerCase('tr')
    return w.toLocaleLowerCase('tr')
  }).join(' ')
}

/**
 * Teklifin canias belge tipi — servis teklifi 'ST', malzeme 'MT'. canias_doctype doluysa o
 * (canias'tan gelen teklifte gerçek tip), yoksa quote_kind. Backend quote_canias_doctype ile aynı.
 * ⚠ SALST/SALMT sayaçları bağımsız: ST-26100002 ile MT-26100002 AYRI belgelerdir.
 */
export const quoteDocType = (q) => {
  if (!q) return 'MT'
  const dt = String(q.canias_doctype || '').trim().toUpperCase()
  if (dt) return dt
  return String(q.quote_kind || '').trim().toLowerCase() === 'service' ? 'ST' : 'MT'
}

/** Servis teklifi mi (canias ST)? */
export const isServiceQuote = (q) => quoteDocType(q) === 'ST' || quoteDocType(q) === 'YST'

/**
 * Teklif numarası gösterimi — canias numarası varsa belge tipi önekiyle
 * (ST-26100001 servis / MT-26060190 malzeme); yoksa taslak VTX-Q numarası.
 * @param {object} q  quote (canias_quote_no, canias_doctype, quote_kind, quote_number)
 * @returns {string}
 */
export const quoteDisplayNo = (q) => {
  if (!q) return ''
  return q.canias_quote_no ? `${quoteDocType(q)}-${q.canias_quote_no}` : (q.quote_number || '')
}

/**
 * Teklif silinebilir mi? — satışçı MÜŞTERİYE GÖNDERMEDİĞİ sürece silinebilir.
 * Sunucudaki `quote_service.DELETABLE_UNSENT_STATUSES` + gönderim damgalarıyla AYNI kural:
 * ayrışırsa ekran çöp kutusunu gösterir, istek 409 döner (ya da tersi).
 * Gönderilmiş / görüntülenmiş / imzalanmış / siparişe dönmüş teklif 'Kayıp' ile kapatılır.
 * @param {object} q quote (status, sent_at, viewed_at, signed_at)
 */
export const QUOTE_DELETABLE_STATUSES = ['draft', 'pending_approval', 'approved', 'rejected', 'revised', 'expired']
export const quoteDeletable = (q) =>
  !!q && QUOTE_DELETABLE_STATUSES.includes(q.status) && !q.sent_at && !q.viewed_at && !q.signed_at

/**
 * Format phone number
 * @param {string} phone
 * @returns {string}
 */
export const formatPhone = (phone) => {
  if (!phone) return '-'
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

/**
 * Format email for display (truncate long addresses)
 * @param {string} email
 * @param {number} maxLength
 * @returns {string}
 */
export const formatEmail = (email, maxLength = 30) => {
  if (!email) return '-'
  return truncateText(email, maxLength)
}

/**
 * Firma / müşteri ismi Title Case (Türkçe destekli).
 *
 * canias komple BÜYÜK döndürür; biz "MAXİON JANTAŞ SAN.TİC.A.Ş." yerine
 * "Maxion Jantaş San.Tic.A.Ş." şeklinde göstermek istiyoruz. Yaygın şirket
 * kısaltmaları (A.Ş., LTD., ŞTİ., TİC., SAN., İNŞ., MÜH., VE, İÇ, DIŞ) olduğu
 * gibi korunur.
 */
// Sadece bunlar tamamen BÜYÜK kalır (kanonik kısaltmalar — nokta arası tek harfli)
const KEEP_UPPER = new Set([
  'A.Ş.', 'A.Ş', 'AŞ',
  'A.G.', 'A.G',
  'GMBH', 'LLC', 'INC', 'LTD.', 'LTD',
])

// Bağlaçlar — TAMAMEN küçük (cümle başı hariç)
const LOWERCASE_CONJ = new Set([
  've', 'ile', 'veya', 'da', 'de',
  'iç', 'dış',  // bunlar Title Case olur, ama burada listede tut, ayrıca kontrol edilir
])

const ALL_LOWER = new Set([
  've', 'ile', 'veya',
])

// Türkçe OLMAYAN marka/kelimeler — canias bunları noktasız "I" ile yazar ("SIEMENS"). Türkçe
// küçültme I'yı ı yapar ve "Sıemens" çıkar; bu kelimeler Latin kuralıyla küçültülür.
// Türkçe kelimelerde I gerçekten ı'dır ("KIRAÇ" → "Kıraç"), o yüzden genel kural değişmez.
const LATIN_WORDS = new Set([
  'SIEMENS', 'PHOENIX', 'SCHNEIDER', 'ELECTRIC', 'AUTOMATION', 'INDUSTRIAL', 'INDUSTRY',
  'INDUSTRIES', 'INTERNATIONAL', 'DIGITAL', 'SOLUTIONS', 'TECHNOLOGIES', 'TECHNOLOGY',
  'ENGINEERING', 'LOGISTICS', 'TRADING', 'MITSUBISHI', 'OMRON', 'RITTAL', 'WEIDMULLER',
  'PILZ', 'FESTO', 'DANFOSS', 'EATON', 'HIMEL', 'VIKO', 'LIMITED', 'GROUP', 'SERVICES',
])

export function titleCaseCompany(s) {
  if (!s || typeof s !== 'string') return s
  // Ayırıcıları (space, nokta, tire, slash, virgül) koruyarak kelimelere böl
  let isFirst = true
  return s.replace(/([^\s.\-\/,]+)/g, (word) => {
    const upperWord = word.toLocaleUpperCase('tr-TR')
    const lowerWord = word.toLocaleLowerCase('tr-TR')

    // Bağlaç (VE, İLE, VEYA) — tamamen küçük (cümle başında bile küçük; firma ad standardı)
    if (ALL_LOWER.has(lowerWord)) {
      isFirst = false
      return lowerWord
    }

    // Kanonik büyük (A.Ş., GMBH, LLC, INC) — büyük kalır
    if (KEEP_UPPER.has(upperWord) || KEEP_UPPER.has(upperWord + '.')) {
      isFirst = false
      return upperWord
    }

    // Tek harfli → büyük (M.A.X. gibi)
    if (word.length === 1) {
      isFirst = false
      return upperWord
    }

    // Latin kelime: "SIEMENS" → "Siemens" (Türkçe küçültme "Sıemens" yapardı)
    if (LATIN_WORDS.has(word.toUpperCase())) {
      isFirst = false
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    }

    // Title case: ilk harf büyük, kalan küçük (Türkçe locale ile)
    // Bu hem normal kelimeler hem kısaltmalar (San. → San., Tic. → Tic., Müh. → Müh.) için doğru
    isFirst = false
    return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR')
  })
}


/**
 * Para/miktar GİRDİSİNİ sayıya çevirir — kullanıcı nokta da virgül de yazabilir.
 *
 * Eski davranış tüm noktaları binlik ayırıcı sayıp SİLİYORDU: "54.7" → 547 (kargo tutarı
 * 10 katına çıkıyordu — 2026-08-20 bildirimi). Kural artık şöyle:
 *   "1.250,50" → 1250.5   (iki ayırıcı: SONDAKİ ondalıktır)
 *   "1250,50"  → 1250.5   (tek virgül → ondalık)
 *   "54.7"     → 54.7     (tek nokta, ondalık kısmı 3 hane DEĞİL → ondalık)
 *   "1.250"    → 1250     (tek nokta + tam 3 hane → binlik; TR yazımı)
 *   "1.250.000"→ 1250000  (çok nokta → binlik)
 * Geçersiz/boş girdide null döner (0 DEĞİL — "yazılmadı" ile "sıfır" ayrışsın).
 */
export function parseMoneyInput(raw) {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  let s = String(raw).replace(/[^\d.,-]/g, '').trim()
  if (!s || s === '-') return null
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.')
    else s = s.replace(/,/g, '')
  } else if (lastComma > -1) {
    s = s.replace(/,/g, '.')
  } else if (lastDot > -1) {
    const dots = (s.match(/\./g) || []).length
    const decimals = s.length - lastDot - 1
    if (dots > 1 || decimals === 3) s = s.replace(/\./g, '')   // binlik yazımı
  }
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/** Sayıyı TR para girdisi biçimine çevirir ("54,70") — alandan çıkınca normalize etmek için. */
export function formatMoneyInput(n, decimals = 2) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return ''
  return Number(n).toFixed(decimals).replace('.', ',')
}

/**
 * Türkçe çoğul eki — ünlü uyumuna göre "-lar" / "-ler".
 * Kelimenin SON ünlüsü belirler: kalın (a, ı, o, u) → "lar", ince (e, i, ö, ü) → "ler".
 * Sabit "ler" eklemek "Faturaler" gibi yanlış yazımlar üretiyordu.
 */
export function pluralTr(word) {
  const w = String(word || '').trim()
  if (!w) return ''
  const BACK = 'aıouAIOU'          // kalın ünlüler (I = büyük ı)
  const FRONT = 'eiöüEİÖÜ'         // ince ünlüler (İ = büyük i)
  for (let i = w.length - 1; i >= 0; i--) {
    if (BACK.includes(w[i])) return `${w}lar`
    if (FRONT.includes(w[i])) return `${w}ler`
  }
  return `${w}lar`                 // ünlü yoksa (kısaltma) kalın varsayılan
}

/**
 * Para birimi ETİKETİ — kullanıcıya gösterilecek metin.
 * Sistem içinde Türk lirası ISO koduyla ('TRY') saklanır (canias eşlemesi ve
 * hesaplamalar buna dayanır), ama ekranda/PDF'te "TL" yazar (kullanıcı isteği).
 * Yalnız GÖSTERİM içindir — kaydedilen/gönderilen değeri bununla değiştirme.
 */
export function currencyLabel(code) {
  const c = String(code || '').trim().toUpperCase()
  return c === 'TRY' ? 'TL' : (c || '')
}

/**
 * Firma rozeti — bildirimlerde/listelerde "bu kayıt hangi firmadan" bilgisi.
 * Renk firmaya SABİTTİR: kullanıcı iki firmada da çalıştığı için, bildirimin
 * hangi taraftan geldiği yazıyı okumadan da anlaşılmalı (kullanıcı isteği).
 */
export function companyBadge(code) {
  const c = String(code || '').trim()
  if (c === '01') {
    return { label: 'Adeptek', cls: 'bg-violet-100 text-violet-700 border-violet-200' }
  }
  if (c === '02') {
    return { label: 'Forte', cls: 'bg-amber-100 text-amber-700 border-amber-200' }
  }
  if (c === '*') {
    return { label: 'Tümü', cls: 'bg-gray-100 text-gray-600 border-gray-200' }
  }
  return c ? { label: c, cls: 'bg-gray-100 text-gray-600 border-gray-200' } : null
}

/**
 * API yanıtından LİSTE türetir — sırayla ilk DİZİ olan aday döner, yoksa [].
 *
 * ⚠️ `a?.items || a || []` deseni bir uç nesne döndürdüğünde ÇÖKER: nesne
 * truthy olduğu için `|| []` yedeği devreye girmez, `.map is not a function`
 * sayfanın tamamını beyaz ekrana çevirir. Bir kez yaşandı (01.10.2026):
 * `siemensService.listDivisions` yanlışlıkla `{divisions, can_edit}` döndüren
 * uca bağlandı ve Ürün Kataloğu dahil 4 sayfa birden çöktü.
 */
export function asList(...candidates) {
  for (const c of candidates) if (Array.isArray(c)) return c
  return []
}
